import { FC, ReactElement, useMemo, useState } from "react";
import { Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useMutateSendFirstSaleEmailMutation } from "@/services/dashboard/dashboard-api";
import { useSnackbar } from "@/hooks";
import StandardConfirmationModal from "@/components/modal/standard-confirmation";
import { useNavigate } from "react-router-dom";
import { roles } from "@/roles";
import { useAppSelector } from "@/store";
import { DynamicTable } from "@/components";
import { createFirstSaleColumns } from "@/columns/COMMON/first-sale";
import SavingsCalculator from "./saving-calculator";
import FirstSaleProgress from "./first-sale-progress";
import ContactVendor from "./contact-vendor";

interface FirstSaleTableProps {
  data: FirstSaleEntry[];
  isLoading: boolean;
  userClientId: string;
  onRefetch?: () => void;
  // Pagination props
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const FirstSaleTable: FC<FirstSaleTableProps> = ({
  data = [],
  isLoading,
  userClientId,
  onRefetch,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}): ReactElement => {
  const [resendOpen, setResendOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [progressOpen, setProgressOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<FirstSaleEntry | null>(null);
  const [sendFirstSaleEmail, { isLoading: isSending }] =
    useMutateSendFirstSaleEmailMutation();
  const { show } = useSnackbar();

  const tableData = useMemo(() => {
    return data.length > 0 ? data : [];
  }, [data]);

  const handleSavingsClick = (item: FirstSaleEntry) => {
    setCurrentItem(item);
    setCalculatorOpen(true);
  };

  const handleActionClick = (item: FirstSaleEntry) => {
    const status = item?.submissionStatus?.[0]?.status || "Not contacted";

    if (status === "Contacted") {
      setCurrentItem(item);
      setResendOpen(true);
    } else if (status === "Vendor response received") {
      navigate(
        `/dashboard/validate/${item.manufacturer}?clientId=${userClientId}${
          user?.userRole === roles.TDADMIN
            ? `&tenantId=${selectedTenant?._id}`
            : ""
        }`
      );
    } else {
      setCurrentItem(item);
      setContactOpen(true);
    }
  };

  const handleStatusClick = (item: FirstSaleEntry) => {
    setCurrentItem(item);
    setProgressOpen(true);
  };

  const handleResendConfirm = async (currentItem: FirstSaleEntry) => {
    try {
      await sendFirstSaleEmail({
        manufacturerId: currentItem.manufacturer || "",
        clientId: currentItem.clientId || "",
        ...(user?.userRole === roles.TDADMIN && {
          tenantId: selectedTenant?._id,
        }),
        to: currentItem.manufacturerEmail || "",
        manufacturerSubmissionId: currentItem._id || "",
      });
      show(
        "Contact form resent successfully",
        "The contact form has been resent to the vendor",
        "success"
      );
      setResendOpen(false);

      // Call refetch after successful email resending
      if (onRefetch) {
        onRefetch();
      }
    } catch (err) {
      show("Failed to resend contact form", "Please try again", "error");
    }
  };

  return (
    <>
      <DynamicTable
        data={tableData}
        stickyHeader
        isLoading={isLoading}
        columns={createFirstSaleColumns(
          handleStatusClick,
          handleActionClick,
          handleSavingsClick
        )}
        sx={{
          flex: 1,
          "& .MuiTable-root": {
            borderCollapse: "collapse",
            overflowY: "auto",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            color: "#030712 !important",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
        }}
        handlePageChange={onPageChange}
        handleLimitChange={onPageSizeChange}
        limit={pageSize}
        page={currentPage}
        total={totalItems}
        hidePagination={false}
      />

      {currentItem && (
        <>
          <SavingsCalculator
            open={calculatorOpen}
            onClose={() => setCalculatorOpen(false)}
            currentValue={currentItem.tariffValue || 0}
            currentDuty={currentItem.tariffDuty || 0}
          />
          <ContactVendor
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            userClientId={userClientId}
            manufacturerName={currentItem.manufacturerName}
            manufacturerId={currentItem.manufacturer || ""}
            sendFirstSaleEmail={sendFirstSaleEmail}
            tenantId={
              user?.userRole === roles.TDADMIN ? selectedTenant?._id : undefined
            }
            isSending={isSending}
            onRefetch={onRefetch}
          />
          <FirstSaleProgress
            open={progressOpen}
            onClose={() => setProgressOpen(false)}
            status={
              currentItem.submissionStatus?.[0]?.status || "Not contacted"
            }
            userClientId={userClientId}
            manufacturerId={currentItem.manufacturer || ""}
            manufacturerName={currentItem.manufacturerName}
            openContactVendor={() => setContactOpen(true)}
            openResendContactForm={() => setResendOpen(true)}
          />
          <StandardConfirmationModal
            open={resendOpen}
            onClose={() => setResendOpen(false)}
            onConfirm={() => handleResendConfirm(currentItem)}
            icon={
              <Box
                sx={{
                  backgroundColor: "rgba(220, 252, 231, 1)",
                  width: "48px",
                  height: "48px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "50%",
                  color: "rgba(22, 163, 74, 1)",
                }}
              >
                <CheckIcon />
              </Box>
            }
            confirmButtonColor="success"
            title="Resend Contact Form"
            description="Are you sure you want to resend the contact form?"
            maxWidth="xs"
          />
        </>
      )}
    </>
  );
};

export default FirstSaleTable;
